import type {
  Model,
  Modality,
  PriceConfidence,
  QualityRank,
  CapabilityStrengths,
} from "../types";
import { comparableUsd } from "./format";

/** Columns that can drive catalog ordering (DataGrid + toolbar). */
export type SortColumn =
  | "name"
  | "modality"
  | "quality"
  | "price"
  | "status"
  | "confidence"
  | "community"
  | "verified"
  | "provider";

export type SortDir = "asc" | "desc";

/**
 * Single source of truth for catalog order.
 * Legacy aliases kept for Jev guide / command rail:
 *   "verified" ≡ verified desc (newest first)
 *   "price-asc" / "price-desc" for price
 */
export type CatalogSort =
  | "name"
  | "name-desc"
  | "modality"
  | "modality-desc"
  | "quality"
  | "quality-desc"
  | "price-asc"
  | "price-desc"
  | "status"
  | "status-desc"
  | "confidence"
  | "confidence-desc"
  | "community"
  | "community-desc"
  | "verified"
  | "verified-asc"
  | "verified-desc"
  | "provider"
  | "provider-desc";

export interface CatalogFilters {
  q: string;
  modality: Modality | "all";
  providerId: string | "all";
  capability: string | "all";
  strength: keyof CapabilityStrengths | "all";
  qualityRank: QualityRank | "all";
  confidence: PriceConfidence | "all";
  maxPrice: number | null;
  status: string | "all";
  sort: CatalogSort;
  /** Intent name-only filter (substring across model/provider names). */
  nameQuery: string;
  /** Intent allow-list of model ids (from fuzzy name match). */
  modelIds: string[] | null;
}

export const defaultFilters: CatalogFilters = {
  q: "",
  modality: "all",
  providerId: "all",
  capability: "all",
  strength: "all",
  qualityRank: "all",
  confidence: "all",
  maxPrice: null,
  status: "all",
  sort: "name",
  nameQuery: "",
  modelIds: null,
};

export const CATALOG_SORT_VALUES: readonly CatalogSort[] = [
  "name",
  "name-desc",
  "modality",
  "modality-desc",
  "quality",
  "quality-desc",
  "price-asc",
  "price-desc",
  "status",
  "status-desc",
  "confidence",
  "confidence-desc",
  "community",
  "community-desc",
  "verified",
  "verified-asc",
  "verified-desc",
  "provider",
  "provider-desc",
] as const;

const QUALITY_RANK = { sota: 0, good: 1, usable: 2 } as const;
const STATUS_RANK: Record<string, number> = {
  ga: 0,
  preview: 1,
  beta: 2,
  deprecated: 3,
};
const CONFIDENCE_RANK: Record<string, number> = {
  verified: 0,
  estimated: 1,
  unknown: 2,
};
const MODALITY_RANK: Record<string, number> = {
  image: 0,
  video: 1,
  both: 2,
};

export function parseSort(sort: CatalogSort): { key: SortColumn; dir: SortDir } {
  if (sort === "price-asc") return { key: "price", dir: "asc" };
  if (sort === "price-desc") return { key: "price", dir: "desc" };
  // Legacy: bare "verified" = newest first
  if (sort === "verified") return { key: "verified", dir: "desc" };
  if (sort.endsWith("-desc")) {
    return { key: sort.slice(0, -5) as SortColumn, dir: "desc" };
  }
  if (sort.endsWith("-asc")) {
    return { key: sort.slice(0, -4) as SortColumn, dir: "asc" };
  }
  return { key: sort as SortColumn, dir: "asc" };
}

export function formatSort(key: SortColumn, dir: SortDir): CatalogSort {
  if (key === "price") return dir === "asc" ? "price-asc" : "price-desc";
  if (dir === "desc") return `${key}-desc` as CatalogSort;
  return key as CatalogSort;
}

/**
 * Header click cycle: other column → asc; same asc → desc; same desc → clear (name).
 */
export function cycleSort(current: CatalogSort, column: SortColumn): CatalogSort {
  const { key, dir } = parseSort(current);
  if (key !== column) return formatSort(column, "asc");
  if (dir === "asc") return formatSort(column, "desc");
  return "name";
}

export function isCatalogSort(v: string): v is CatalogSort {
  return (CATALOG_SORT_VALUES as readonly string[]).includes(v);
}

function cmpNullLast(
  a: number | null | undefined,
  b: number | null | undefined,
  dir: SortDir,
): number {
  const aNull = a == null || Number.isNaN(a);
  const bNull = b == null || Number.isNaN(b);
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  return dir === "asc" ? a! - b! : b! - a!;
}

function mul(dir: SortDir): number {
  return dir === "asc" ? 1 : -1;
}

export function filterModels(
  models: Model[],
  f: CatalogFilters,
  providerName: (id: string) => string,
): Model[] {
  const out = models.filter((m) => {
    if (f.modality !== "all" && m.modality !== f.modality) return false;
    if (f.providerId !== "all" && m.providerId !== f.providerId) return false;
    if (f.capability !== "all" && !m.capabilities.includes(f.capability))
      return false;
    if (f.strength !== "all") {
      if (!m.strengths?.[f.strength]) return false;
    }
    if (f.qualityRank !== "all" && m.qualityRank !== f.qualityRank) return false;
    if (f.confidence !== "all" && m.pricing.confidence !== f.confidence)
      return false;
    if (f.status !== "all" && m.status !== f.status) return false;
    if (f.maxPrice != null) {
      const cmp = comparableUsd(m);
      if (cmp == null) return false;
      if (cmp > f.maxPrice) return false;
    }
    if (f.modelIds && f.modelIds.length > 0) {
      if (!f.modelIds.includes(m.id)) return false;
    }
    if (f.nameQuery.trim() && !(f.modelIds && f.modelIds.length > 0)) {
      const nq = f.nameQuery.trim().toLowerCase();
      const hay = [
        m.name,
        m.id,
        providerName(m.providerId),
        ...(m.aliases ?? []),
      ]
        .join(" ")
        .toLowerCase();
      if (!hay.includes(nq)) return false;
    }
    if (f.q.trim()) {
      const q = f.q.trim().toLowerCase();
      const hay = [
        m.name,
        m.id,
        providerName(m.providerId),
        m.pricing.raw,
        ...(m.aliases ?? []),
        ...m.capabilities,
        m.notes ?? "",
        m.qualityRank ?? "",
        m.endpointId ?? "",
      ]
        .join(" ")
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const { key, dir } = parseSort(f.sort);
  const m = mul(dir);

  return [...out].sort((a, b) => {
    let primary = 0;
    switch (key) {
      case "price":
        return cmpNullLast(comparableUsd(a), comparableUsd(b), dir);
      case "provider":
        primary = providerName(a.providerId).localeCompare(
          providerName(b.providerId),
        );
        break;
      case "verified":
        primary = a.lastVerified.localeCompare(b.lastVerified);
        break;
      case "quality":
        primary =
          (QUALITY_RANK[a.qualityRank ?? "usable"] ?? 9) -
          (QUALITY_RANK[b.qualityRank ?? "usable"] ?? 9);
        break;
      case "modality":
        primary =
          (MODALITY_RANK[a.modality] ?? 9) - (MODALITY_RANK[b.modality] ?? 9);
        break;
      case "status":
        primary =
          (STATUS_RANK[a.status] ?? 9) - (STATUS_RANK[b.status] ?? 9);
        break;
      case "confidence":
        primary =
          (CONFIDENCE_RANK[a.pricing.confidence] ?? 9) -
          (CONFIDENCE_RANK[b.pricing.confidence] ?? 9);
        break;
      case "community":
        return cmpNullLast(a.communityScore, b.communityScore, dir);
      case "name":
      default:
        primary = a.name.localeCompare(b.name);
        break;
    }
    if (primary !== 0) return primary * m;
    // Stable secondary: name asc
    return a.name.localeCompare(b.name);
  });
}
